// listContextFromGridState — derive a v2 `list_context` from a live office grid.
//
// This is the client-side twin of the Ruby `EmitsPageContext::DatatableAdapter`
// (app/controllers/concerns/emits_page_context.rb). The Rails adapter produces
// list_context at HTML-render time (before the grid loads, so it can only see
// the default/unfiltered set). This mapper runs in the browser against the LIVE
// grid reducer state, so it reflects exactly what the user currently sees:
// applied filters, sort, page, the filtered count, and the visible row IDs.
//
// Filter shape: the office index features (InspectionsIndex, BuildingsIndex,
// WorkOrdersIndex, ProposalsIndex, …) store filters as the FilterPanel/
// kendoDatatableParams shape:
//
//     filters = { values: { field: value }, operators: { field: 'eq' | … } }
//
// where `value` is a scalar, a multi-select array, or a date-range object
// ({ startDate, endDate, rangeType }). A plain `{ field: value }` object (used
// by a couple of simpler indexes) is also accepted.
//
// SQL-reproducibility contract (matches the Ruby adapter + orchestrator
// ListContext model). `active_filters` values may be:
//   - a scalar        → `column = value`   (null → `column IS NULL`)
//   - an array         → `column IN (…)`     (multi-select filters)
//   - a { from, to }   → `column BETWEEN …`  (date-range filters; one-sided
//                        when only one bound is set)
// These three shapes are SQL-reproducible, so multi-select and date-range
// filters no longer flip `filters_complete` to false (IP-14548 — dropping them
// made IPA unable to answer "how many of these" on any realistically-filtered
// list, since nearly every grid filter is a multi-select). Only genuinely
// non-reproducible state flips `filters_complete` to false: non-eq text
// operators (e.g. `contains`), global text search, arrays with non-scalar
// members, and the key/element caps. When incomplete, IPA still gets the
// captured filters as partial context but is told not to report an exact count.
const MAX_ACTIVE_FILTERS = 40;
const MAX_VISIBLE_IDS = 200;
const SORT_MAX_LENGTH = 64;
// Cap on the number of values kept for a single multi-select (IN) filter. A
// multi-select with more entries than this is truncated and marks the filter set
// incomplete so IPA won't over-trust a reproduced count. Raised from 100 to
// 5000 (IP-14548 lazy-load handle): real "select all" multi-selects run into
// the thousands, and the orchestrator now keeps the full set in state
// (get_current_list_context tool) rather than rendering it into the prompt,
// so this is a safety ceiling, not a realistic selection-size limit.
const MAX_FILTER_ARRAY_VALUES = 5000;
function isScalar(v) {
    return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}
function isDateRange(v) {
    return (typeof v === 'object' &&
        v !== null &&
        ('startDate' in v || 'endDate' in v || 'start' in v || 'end' in v || 'rangeType' in v));
}
// Two bound-key spellings are in play and both must be read here. The legacy
// Kendo grids carry `{ startDate, endDate, rangeType }`; the Quimby-rewritten
// listings normalize to `{ start, end, rangeType }` (see
// InspectionsIndex/types.ts `InspectionDateRange`) and only translate back to
// `startDate`/`endDate` at the wire boundary in their request serializer.
//
// Reading only the legacy spelling silently dropped every date-range filter on
// the rewritten listings: `isDateRange` still matched on `rangeType`, then the
// emptiness check read two keys that are always `undefined` on the new shape and
// so reported "not applied" no matter what the user had selected. The filter
// never reached `active_filters`, and the assistant answered as though the user
// had set no date range at all — on the Inspections page, where `scheduledDate`
// is on by default, that is the single most common filter there is.
function dateRangeBounds(v) {
    var _a, _b;
    const r = v;
    return { from: (_a = r.startDate) !== null && _a !== void 0 ? _a : r.start, to: (_b = r.endDate) !== null && _b !== void 0 ? _b : r.end };
}
// What counts as a set bound. Shared by isEmptyDateRange and toDateRangeFilter
// so the two can't disagree: a whitespace-only bound (which legacy Kendo
// preferences can preserve) would otherwise read as "set" to the emptiness check
// and as null to the reducer, publishing an unapplied `{ from: null, to: null }`
// range into active_filters.
function hasBound(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
// A date-range filter with neither bound set is not actually applied — the Rails
// kendoDatatableParams serializer skips it (`!startDate && !endDate`). Treat it
// as empty so an unset date-range control doesn't falsely flip filters_complete.
function isEmptyDateRange(v) {
    if (!isDateRange(v))
        return false;
    const { from, to } = dateRangeBounds(v);
    return !hasBound(from) && !hasBound(to);
}
function isEmpty(v) {
    return v === null || v === undefined || v === '';
}
// Reduce a date-range control to the SQL-reproducible { from, to } bounds,
// accepting either bound-key spelling (see dateRangeBounds). Blank bounds become
// null (one-sided).
function toDateRangeFilter(v) {
    const { from, to } = dateRangeBounds(v);
    const bound = (d) => (hasBound(d) ? d : null);
    return { from: bound(from), to: bound(to) };
}
/**
 * Reduce the filter state to reproducible `field => value` pairs. Values are
 * scalars (`=`), arrays (`IN`), or `{ from, to }` date ranges (`BETWEEN`).
 * `complete` is false only when a filter cannot be reproduced from those shapes:
 * a non-eq text operator (e.g. `contains`), an array whose members aren't all
 * scalar, a truncated over-cap array, a non-scalar/non-range value, or the key
 * cap. Values that flip `complete` are still captured (partial context) except
 * where noted.
 */
function flattenFilters(filters) {
    var _a, _b, _c;
    const active = {};
    let complete = true;
    if (!filters || typeof filters !== 'object')
        return { active, complete };
    const maybe = filters;
    const values = (_a = maybe.values) !== null && _a !== void 0 ? _a : filters;
    const operators = (_b = maybe.operators) !== null && _b !== void 0 ? _b : {};
    for (const [field, value] of Object.entries(values)) {
        // Skip unset values and unset date-range controls (an empty date range is
        // not an applied filter, so it must not flip filters_complete).
        if (isEmpty(value) || isEmptyDateRange(value))
            continue;
        if (Object.keys(active).length >= MAX_ACTIVE_FILTERS) {
            complete = false;
            break;
        }
        // Multi-select → IN clause. Reproducible as `column IN (…)`.
        if (Array.isArray(value)) {
            const scalars = value.filter(isScalar);
            if (scalars.length === 0) {
                // Array of non-scalars (unexpected) — can't reproduce.
                complete = false;
                continue;
            }
            // Some members weren't scalar, or the list is too long to forward fully.
            if (scalars.length !== value.length)
                complete = false;
            if (scalars.length > MAX_FILTER_ARRAY_VALUES) {
                active[field] = scalars.slice(0, MAX_FILTER_ARRAY_VALUES);
                complete = false;
            }
            else {
                active[field] = scalars;
            }
            continue;
        }
        // Date range → BETWEEN. Reproducible as `column BETWEEN from AND to`.
        if (isDateRange(value)) {
            active[field] = toDateRangeFilter(value);
            continue;
        }
        if (!isScalar(value)) {
            // Nested object or other non-reproducible shape.
            complete = false;
            continue;
        }
        // Scalar. Only `eq` is exactly reproducible; a non-eq text operator
        // (e.g. `contains`) is kept as partial context but flips completeness.
        const operator = (_c = operators[field]) !== null && _c !== void 0 ? _c : 'eq';
        if (operator !== 'eq')
            complete = false;
        active[field] = value;
    }
    return { active, complete };
}
function buildSort(sort) {
    if (!Array.isArray(sort) || sort.length === 0)
        return undefined;
    const parts = sort.filter((s) => s && s.field && s.dir).map((s) => `${s.field} ${s.dir}`);
    if (parts.length === 0)
        return undefined;
    return parts.join(', ').slice(0, SORT_MAX_LENGTH);
}
function buildVisibleIds(data) {
    if (!Array.isArray(data) || data.length === 0)
        return undefined;
    const ids = data
        .map((row) => (row && typeof row.id === 'number' ? row.id : undefined))
        .filter((id) => typeof id === 'number');
    if (ids.length === 0)
        return undefined;
    return ids.slice(0, MAX_VISIBLE_IDS);
}
function hasGlobalSearch(search) {
    if (typeof search === 'string')
        return search.trim().length > 0;
    // Some SearchPanels store an object — treat any truthy value field as a search.
    if (search && typeof search === 'object') {
        const v = search.value;
        return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
    }
    return false;
}
/**
 * Map a live office grid reducer slice to a v2 `list_context`.
 *
 * Returns `null` when there is no meaningful state to publish so callers can
 * clear the bus instead of publishing a hollow payload.
 */
export function listContextFromGridState(state) {
    var _a, _b, _c, _d;
    if (!state)
        return null;
    const take = (_b = (_a = state.page) === null || _a === void 0 ? void 0 : _a.take) !== null && _b !== void 0 ? _b : null;
    const skip = (_d = (_c = state.page) === null || _c === void 0 ? void 0 : _c.skip) !== null && _d !== void 0 ? _d : null;
    const { active, complete } = flattenFilters(state.filters);
    const filtersComplete = complete && !hasGlobalSearch(state.search);
    const sort = buildSort(state.sort);
    const visibleIds = buildVisibleIds(state.data);
    const hasActiveFilters = Object.keys(active).length > 0;
    const listContext = { filters_complete: filtersComplete };
    if (typeof state.totalCount === 'number')
        listContext.filtered_count = state.totalCount;
    if (hasActiveFilters)
        listContext.active_filters = active;
    if (sort)
        listContext.sort = sort;
    if (typeof take === 'number' && take > 0) {
        listContext.page_size = take;
        if (typeof skip === 'number' && skip >= 0) {
            listContext.page = Math.floor(skip / take) + 1;
        }
    }
    if (visibleIds)
        listContext.visible_ids = visibleIds;
    if (listContext.filtered_count === undefined &&
        !hasActiveFilters &&
        !sort &&
        listContext.page === undefined &&
        !visibleIds) {
        return null;
    }
    return listContext;
}
